import { useCallback, useEffect, useRef, useState, type ClipboardEvent, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ChevronLeftIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { AdminApi, AdminAudience, AdminCourse, AdminCourseGiftRule, AdminCourseUpsertPayload } from '../../services/adminApi'
import { toast } from 'react-toastify'
import AdminRichTextEditor from '../../components/admin/AdminRichTextEditor'
import type { LandingSections } from '../../types/course'

const dataUrlToFile = async (dataUrl: string, filename: string): Promise<File> => {
  const res = await fetch(dataUrl)
  const blob = await res.blob()
  return new File([blob], filename, { type: blob.type || 'image/png' })
}

const replaceBase64ImagesInHtml = async (html: string): Promise<string> => {
  if (!html || !html.includes('data:image')) return html
  const regex = /src=(["'])(data:image\/[a-zA-Z0-9+.-]+;base64,[^"']+)\1/gi
  const matches = [...html.matchAll(regex)]
  if (!matches.length) return html

  let next = html
  for (let i = 0; i < matches.length; i += 1) {
    const dataUrl = matches[i][2]
    try {
      const file = await dataUrlToFile(dataUrl, `paste-${Date.now()}-${i}.png`)
      const res = await AdminApi.uploadImage(file)
      if (res.success && res.data?.url) {
        next = next.split(dataUrl).join(res.data.url)
      }
    } catch (err) {
      console.error('Failed to upload pasted image', err)
    }
  }
  return next
}

type FormState = {
  code: string
  name: string
  method: string
  fee: string
  status: string
  isActive: number
  timeInHour: string
  timeInLesson: string
  imageUrl: string
  slug: string
  metaDescription: string
  openGraphImageUrl: string
  tags: string
  shortDescription: string
  requiredInput: string
  suitableObject: string
  rule: string
  content: string
  lecturer: string
  detailIntroduce: string
}

const emptyForm: FormState = {
  code: '',
  name: '',
  method: 'VIDEO',
  fee: '0',
  status: 'DRAFT',
  isActive: 1,
  timeInHour: '0',
  timeInLesson: '',
  imageUrl: '',
  slug: '',
  metaDescription: '',
  openGraphImageUrl: '',
  tags: '',
  shortDescription: '',
  requiredInput: '',
  suitableObject: '',
  rule: '',
  content: '',
  lecturer: '',
  detailIntroduce: '',
}

const HTML_FIELDS: { key: keyof FormState; label: string }[] = [
  { key: 'requiredInput', label: 'Yêu cầu tham gia' },
  { key: 'suitableObject', label: 'Đối tượng phù hợp' },
  { key: 'rule', label: 'Quy định khóa học' },
  { key: 'content', label: 'Nội dung khóa học' },
  { key: 'lecturer', label: 'Giảng viên HTML (fallback nếu chưa điền block giảng viên structured)' },
  { key: 'detailIntroduce', label: 'Giới thiệu chi tiết / giảng viên' },
]

/** Convert stored HTML/plain shortDescription into one-item-per-line text for admin textarea. */
function shortDescriptionToLines(raw: string): string {
  if (!raw?.trim()) return ''
  const liMatches = [...raw.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)]
  if (liMatches.length > 0) {
    return liMatches
      .map((m) =>
        m[1]
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .trim()
      )
      .filter(Boolean)
      .join('\n')
  }
  return raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .join('\n')
}

const AdminCourseFormPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = Boolean(id)
  const courseId = id ? parseInt(id, 10) : 0

  const [form, setForm] = useState<FormState>(emptyForm)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [uploadingCover, setUploadingCover] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [audiences, setAudiences] = useState<AdminAudience[]>([])
  const [selectedAudienceIds, setSelectedAudienceIds] = useState<number[]>([])
  const [primaryAudienceId, setPrimaryAudienceId] = useState<number | null>(null)
  const [landing, setLanding] = useState<LandingSections>({
    heroHeadline: '',
    heroSubheadline: '',
    painPoints: [],
    outcomes: [],
    beforeAfter: [],
    demoUrls: [],
    faqs: [],
    instructor: {},
  })
  const [showLanding, setShowLanding] = useState(true)
  const [uploadingInstructor, setUploadingInstructor] = useState(false)
  const [allCourses, setAllCourses] = useState<AdminCourse[]>([])
  const [giftRules, setGiftRules] = useState<Array<{ giftCourseId: number; isActive: boolean }>>([])
  const [savingGifts, setSavingGifts] = useState(false)
  const coverInputRef = useRef<HTMLInputElement>(null)
  const instructorInputRef = useRef<HTMLInputElement>(null)
  const base64TimersRef = useRef<Record<string, number>>({})

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  useEffect(() => {
    AdminApi.getAudiences()
      .then((res) => {
        if (res.success && res.data?.items) setAudiences(res.data.items.filter((a) => a.isActive))
      })
      .catch(() => toast.error('Không tải được danh sách đối tượng'))
  }, [])

  useEffect(() => {
    if (!isEdit || !courseId) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await AdminApi.getCourseById(courseId)
        if (!res.success || !res.data) {
          toast.error('Không tải được khóa học')
          navigate('/admin/courses')
          return
        }
        const c = res.data
        setForm({
          code: c.code || '',
          name: c.name || '',
          method: (c.method || 'VIDEO').toUpperCase() === 'HYBRID' ? 'HYBIRID' : (c.method || 'VIDEO').toUpperCase(),
          fee: String(c.fee ?? 0),
          status: (c.status || 'DRAFT').toUpperCase() === 'PUBLISHED' ? 'ACTIVE' : (c.status || 'DRAFT').toUpperCase(),
          isActive: c.isActive ?? c.is_active ?? 1,
          timeInHour: String(c.timeInHour ?? 0),
          timeInLesson: c.timeInLesson != null ? String(c.timeInLesson) : '',
          imageUrl: c.imageUrl || '',
          slug: c.slug || '',
          metaDescription: c.metaDescription || '',
          openGraphImageUrl: c.openGraphImageUrl || '',
          tags: c.tags || '',
          shortDescription: shortDescriptionToLines(c.shortDescription || ''),
          requiredInput: c.requiredInput || '',
          suitableObject: c.suitableObject || '',
          rule: c.rule || '',
          content: c.content || '',
          lecturer: c.lecturer || '',
          detailIntroduce: c.detailIntroduce || '',
        })
        setSelectedAudienceIds(c.audienceIds || c.audiences?.map((a) => a.id) || [])
        setPrimaryAudienceId(c.primaryAudienceId ?? c.audiences?.find((a) => a.isPrimary)?.id ?? null)
        setLanding({
          heroHeadline: c.landingSections?.heroHeadline || '',
          heroSubheadline: c.landingSections?.heroSubheadline || '',
          painPoints: c.landingSections?.painPoints || [],
          outcomes: c.landingSections?.outcomes || [],
          beforeAfter: c.landingSections?.beforeAfter || [],
          demoUrls: c.landingSections?.demoUrls || [],
          faqs: c.landingSections?.faqs || [],
          instructor: c.landingSections?.instructor || {},
        })

        try {
          const [coursesRes, giftsRes] = await Promise.all([
            AdminApi.getCourses(),
            AdminApi.getCourseGiftRules(courseId),
          ])
          if (coursesRes.success && coursesRes.data?.items) {
            setAllCourses(coursesRes.data.items.filter((x) => x.id !== courseId))
          }
          const items: AdminCourseGiftRule[] = giftsRes.success && giftsRes.data?.items ? giftsRes.data.items : []
          setGiftRules(
            items.map((g) => ({
              giftCourseId: g.giftCourseId,
              isActive: g.isActive !== false,
            }))
          )
        } catch {
          /* gift rules optional until migration */
        }
      } catch (err) {
        console.error(err)
        toast.error('Có lỗi khi tải khóa học')
        navigate('/admin/courses')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEdit, courseId, navigate])

  const uploadImageFile = useCallback(async (file: File): Promise<string> => {
    const res = await AdminApi.uploadImage(file)
    if (!res.success || !res.data?.url) {
      throw new Error(res.message || 'Upload ảnh thất bại')
    }
    return res.data.url
  }, [])

  const handleCoverFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Vui lòng chọn file ảnh')
      return
    }
    try {
      setUploadingCover(true)
      const url = await uploadImageFile(file)
      setField('imageUrl', url)
      toast.success('Đã lưu ảnh cover lên Google Cloud Storage')
    } catch (err: any) {
      toast.error(err?.message || 'Upload ảnh cover thất bại')
    } finally {
      setUploadingCover(false)
    }
  }

  const handleCoverPaste = async (e: ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i]
      if (item.type.startsWith('image/')) {
        e.preventDefault()
        const file = item.getAsFile()
        if (file) await handleCoverFile(file)
        return
      }
    }
  }

  const setInstructor = useCallback(
    (patch: NonNullable<LandingSections['instructor']>) => {
      setLanding((p) => ({
        ...p,
        instructor: { ...(p.instructor || {}), ...patch },
      }))
    },
    []
  )

  const handleInstructorFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Vui lòng chọn file ảnh')
      return
    }
    try {
      setUploadingInstructor(true)
      const url = await uploadImageFile(file)
      setInstructor({ imageUrl: url })
      toast.success('Đã lưu ảnh giảng viên lên Google Cloud Storage')
    } catch (err: any) {
      toast.error(err?.message || 'Upload ảnh giảng viên thất bại')
    } finally {
      setUploadingInstructor(false)
    }
  }

  const handleInstructorPaste = async (e: ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i]
      if (item.type.startsWith('image/')) {
        e.preventDefault()
        const file = item.getAsFile()
        if (file) await handleInstructorFile(file)
        return
      }
    }
  }

  const handleHtmlChange = (key: keyof FormState, html: string) => {
    setField(key, html)
    if (base64TimersRef.current[key]) {
      window.clearTimeout(base64TimersRef.current[key])
    }
    base64TimersRef.current[key] = window.setTimeout(async () => {
      if (!html.includes('data:image')) return
      try {
        setUploadingImage(true)
        const next = await replaceBase64ImagesInHtml(html)
        if (next !== html) setField(key, next)
      } finally {
        setUploadingImage(false)
      }
    }, 600)
  }

  const buildPayload = async (): Promise<AdminCourseUpsertPayload> => {
    const htmlKeys: (keyof FormState)[] = [
      'requiredInput',
      'suitableObject',
      'rule',
      'content',
      'lecturer',
      'detailIntroduce',
    ]
    const htmlValues: Record<string, string> = {}
    for (const key of htmlKeys) {
      htmlValues[key] = await replaceBase64ImagesInHtml(form[key] as string)
    }

    return {
      code: form.code.trim() || null,
      name: form.name.trim(),
      method: form.method,
      fee: Number(form.fee) || 0,
      status: form.status,
      isActive: form.isActive,
      timeInHour: Number(form.timeInHour) || 0,
      timeInLesson: form.timeInLesson === '' ? null : Number(form.timeInLesson),
      imageUrl: form.imageUrl.trim() || null,
      slug: form.slug.trim() || null,
      metaDescription: form.metaDescription.trim() || null,
      openGraphImageUrl: form.openGraphImageUrl.trim() || null,
      tags: form.tags.trim() || null,
      shortDescription:
        form.shortDescription
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean)
          .join('\n') || null,
      requiredInput: htmlValues.requiredInput || null,
      suitableObject: htmlValues.suitableObject || null,
      rule: htmlValues.rule || null,
      content: htmlValues.content || null,
      lecturer: htmlValues.lecturer || null,
      detailIntroduce: htmlValues.detailIntroduce || null,
      audienceIds: selectedAudienceIds,
      primaryAudienceId:
        primaryAudienceId && selectedAudienceIds.includes(primaryAudienceId)
          ? primaryAudienceId
          : selectedAudienceIds[0] ?? null,
      landingSections: {
        heroHeadline: landing.heroHeadline?.trim() || undefined,
        heroSubheadline: landing.heroSubheadline?.trim() || undefined,
        painPoints: (landing.painPoints || []).filter((p) => p.trim()),
        outcomes: (landing.outcomes || []).filter((o) => o.title.trim()),
        beforeAfter: (landing.beforeAfter || []).filter((r) => r.task.trim()),
        demoUrls: (landing.demoUrls || []).filter((u) => u.trim()),
        faqs: (landing.faqs || []).filter((f) => f.q.trim()),
        instructor: (() => {
          const ins = landing.instructor || {}
          const stats = (ins.stats || []).filter((s) => s.value.trim() || s.label.trim())
          const payload = {
            imageUrl: ins.imageUrl?.trim() || undefined,
            imageOverlay: ins.imageOverlay?.trim() || undefined,
            sectionLabel: ins.sectionLabel?.trim() || undefined,
            name: ins.name?.trim() || undefined,
            bio: ins.bio?.trim() || undefined,
            stats: stats.length ? stats : undefined,
          }
          return payload.imageUrl || payload.name || payload.bio || payload.stats || payload.imageOverlay || payload.sectionLabel
            ? payload
            : undefined
        })(),
      },
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      toast.error('Vui lòng nhập tên khóa học')
      return
    }
    try {
      setSaving(true)
      const payload = await buildPayload()
      const res = isEdit
        ? await AdminApi.updateCourse(courseId, payload)
        : await AdminApi.createCourse(payload)
      if (!res.success || !res.data?.id) {
        toast.error(res.message || 'Lưu khóa học thất bại')
        return
      }
      toast.success(isEdit ? 'Cập nhật khóa học thành công' : 'Tạo khóa học thành công')
      navigate(`/admin/courses/${res.data.id}`)
    } catch (err: any) {
      console.error(err)
      toast.error(err?.response?.data?.message || err?.message || 'Có lỗi khi lưu khóa học')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-m2-surface-container p-6 rounded-lg shadow-sm">
        <p>Đang tải...</p>
      </div>
    )
  }

  return (
    <div className="bg-m2-surface-container p-6 rounded-lg shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <Link to={isEdit ? `/admin/courses/${courseId}` : '/admin/courses'} className="text-m2-on-surface-variant hover:text-m2-on-surface">
          <ChevronLeftIcon className="h-6 w-6" />
        </Link>
        <h1 className="text-2xl font-bold text-m2-on-surface">
          {isEdit ? 'Sửa khóa học' : 'Thêm khóa học mới'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <section className="space-y-4">
          <h2 className="text-lg font-semibold text-m2-on-surface border-b pb-2">Thông tin chung</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium text-m2-on-surface">Mã khóa học</span>
              <input
                type="text"
                className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                value={form.code}
                onChange={(e) => setField('code', e.target.value)}
                placeholder="VD: WEB-101"
              />
            </label>
            <label className="block md:col-span-2">
              <span className="text-sm font-medium text-m2-on-surface">
                Tên khóa học <span className="text-red-500">*</span>
              </span>
              <input
                type="text"
                required
                className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-m2-on-surface">Hình thức</span>
              <select
                className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                value={form.method}
                onChange={(e) => setField('method', e.target.value)}
              >
                <option value="VIDEO">Video</option>
                <option value="ONLINE">Trực tuyến</option>
                <option value="OFFLINE">Offline</option>
                <option value="HYBIRID">Hybrid</option>
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-m2-on-surface">Học phí (VND)</span>
              <input
                type="number"
                min={0}
                className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                value={form.fee}
                onChange={(e) => setField('fee', e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-m2-on-surface">Số giờ</span>
              <input
                type="number"
                min={0}
                className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                value={form.timeInHour}
                onChange={(e) => setField('timeInHour', e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-m2-on-surface">Số buổi</span>
              <input
                type="number"
                min={0}
                className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                value={form.timeInLesson}
                onChange={(e) => setField('timeInLesson', e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-m2-on-surface">Trạng thái</span>
              <select
                className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                value={form.status}
                onChange={(e) => setField('status', e.target.value)}
              >
                <option value="DRAFT">Nháp (DRAFT)</option>
                <option value="ACTIVE">Đang mở (ACTIVE)</option>
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-m2-on-surface">Đang mở bán</span>
              <select
                className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                value={form.isActive}
                onChange={(e) => setField('isActive', Number(e.target.value))}
              >
                <option value={1}>Có</option>
                <option value={0}>Không</option>
              </select>
            </label>
          </div>

          <div>
            <span className="text-sm font-medium text-m2-on-surface">Ảnh cover</span>
            <div
              tabIndex={0}
              onPaste={handleCoverPaste}
              className="mt-1 border-2 border-dashed border-m2-outline rounded-lg p-4 min-h-[160px] focus:outline-none focus:ring-2 focus:ring-m2-primary bg-m2-surface-low"
            >
              {form.imageUrl ? (
                <div className="relative inline-block max-w-full">
                  <img src={form.imageUrl} alt="Cover" className="max-h-48 rounded-md object-contain" />
                  <button
                    type="button"
                    onClick={() => setField('imageUrl', '')}
                    className="absolute top-1 right-1 bg-m2-surface/90 rounded-full p-1 shadow hover:bg-m2-surface-container"
                    title="Xóa ảnh"
                  >
                    <XMarkIcon className="h-4 w-4 text-m2-on-surface" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-m2-on-surface-variant py-6">
                  <PhotoIcon className="h-10 w-10 mb-2" />
                  <p className="text-sm">Copy-paste ảnh vào đây — ảnh sẽ được lưu lên Google Cloud Storage.</p>
                </div>
              )}
              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  disabled={uploadingCover}
                  onClick={() => coverInputRef.current?.click()}
                  className="text-sm px-3 py-1.5 bg-m2-surface-container border border-m2-outline rounded-md hover:bg-m2-surface-high disabled:opacity-50"
                >
                  {uploadingCover ? 'Đang tải...' : 'Chọn file ảnh'}
                </button>
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleCoverFile(file)
                    e.target.value = ''
                  }}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <label className="block">
              <span className="text-sm font-medium text-m2-on-surface">Slug (SEO)</span>
              <input
                type="text"
                className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                value={form.slug}
                onChange={(e) => setField('slug', e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-m2-on-surface">Tags (SEO / card)</span>
              <textarea
                className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                rows={2}
                value={form.tags}
                onChange={(e) => setField('tags', e.target.value)}
                placeholder="cách nhau bởi dấu phẩy"
              />
              <span className="mt-1 block text-xs text-m2-on-surface-variant">
                Tags ngắn, cách nhau bởi dấu phẩy (SEO / card khóa học).
              </span>
            </label>
            <label className="block md:col-span-2">
              <span className="text-sm font-medium text-m2-on-surface">Bạn sẽ học được gì</span>
              <textarea
                className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2 font-mono text-sm"
                rows={8}
                value={form.shortDescription}
                onChange={(e) => setField('shortDescription', e.target.value)}
                placeholder={'Mỗi kiến thức một dòng, ví dụ:\nỨng dụng AI vào elicitation yêu cầu\nXây dựng Use Case chuẩn'}
              />
              <span className="mt-1 block text-xs text-m2-on-surface-variant">
                Mỗi dòng = 1 ý. Hiển thị checklist 2 cột với icon ✓ màu xanh trên trang chi tiết khóa học.
              </span>
            </label>
            <label className="block md:col-span-2">
              <span className="text-sm font-medium text-m2-on-surface">Meta description</span>
              <input
                type="text"
                maxLength={255}
                className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                value={form.metaDescription}
                onChange={(e) => setField('metaDescription', e.target.value)}
              />
            </label>
            <label className="block md:col-span-2">
              <span className="text-sm font-medium text-m2-on-surface">Open Graph image URL</span>
              <input
                type="text"
                className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                value={form.openGraphImageUrl}
                onChange={(e) => setField('openGraphImageUrl', e.target.value)}
              />
            </label>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-semibold text-m2-on-surface border-b pb-2">Đối tượng học viên</h2>
          <p className="text-xs text-m2-on-surface-variant">Chọn một hoặc nhiều nhóm; đánh dấu đối tượng chính để hiện trên card.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {audiences.map((a) => {
              const checked = selectedAudienceIds.includes(a.id)
              return (
                <label key={a.id} className="flex items-start gap-3 border rounded-md p-3 cursor-pointer hover:bg-m2-surface-low">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={checked}
                    onChange={() => {
                      setSelectedAudienceIds((prev) => {
                        const next = checked ? prev.filter((id) => id !== a.id) : [...prev, a.id]
                        if (checked && primaryAudienceId === a.id) {
                          setPrimaryAudienceId(next[0] ?? null)
                        } else if (!checked && !primaryAudienceId) {
                          setPrimaryAudienceId(a.id)
                        }
                        return next
                      })
                    }}
                  />
                  <span className="flex-1">
                    <span className="font-medium text-m2-on-surface block">{a.name}</span>
                    <span className="text-xs text-m2-on-surface-variant">{a.shortQuestion || a.code}</span>
                  </span>
                  <button
                    type="button"
                    disabled={!checked}
                    onClick={(e) => {
                      e.preventDefault()
                      setPrimaryAudienceId(a.id)
                    }}
                    className={`text-xs px-2 py-1 rounded border ${
                      primaryAudienceId === a.id
                        ? 'bg-m2-primary text-m2-on-primary border-m2-primary'
                        : 'border-m2-outline text-m2-on-surface-variant disabled:opacity-40'
                    }`}
                  >
                    Chính
                  </button>
                </label>
              )
            })}
          </div>
        </section>

        <section className="space-y-4">
          <button
            type="button"
            onClick={() => setShowLanding((v) => !v)}
            className="w-full flex items-center justify-between text-lg font-semibold text-m2-on-surface border-b pb-2"
          >
            Landing chuyển đổi
            <span className="text-sm font-normal text-m2-on-surface-variant">{showLanding ? 'Thu gọn' : 'Mở rộng'}</span>
          </button>
          {showLanding && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block md:col-span-2">
                  <span className="text-sm font-medium text-m2-on-surface">Hero headline</span>
                  <input
                    type="text"
                    className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                    value={landing.heroHeadline || ''}
                    onChange={(e) => setLanding((p) => ({ ...p, heroHeadline: e.target.value }))}
                  />
                </label>
                <label className="block md:col-span-2">
                  <span className="text-sm font-medium text-m2-on-surface">Hero subheadline</span>
                  <textarea
                    className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                    rows={2}
                    value={landing.heroSubheadline || ''}
                    onChange={(e) => setLanding((p) => ({ ...p, heroSubheadline: e.target.value }))}
                  />
                </label>
              </div>

              <label className="block">
                <span className="text-sm font-medium text-m2-on-surface">Vấn đề học viên đang gặp</span>
                <textarea
                  className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2 font-mono text-sm"
                  rows={4}
                  value={(landing.painPoints || []).join('\n')}
                  onChange={(e) =>
                    setLanding((p) => ({
                      ...p,
                      painPoints: e.target.value.split('\n'),
                    }))
                  }
                  placeholder="Mỗi dòng một vấn đề"
                />
                <span className="mt-1 block text-xs text-m2-on-surface-variant">
                  Mỗi dòng một vấn đề. Hiện ở đầu trang chi tiết để học viên nhận ra khóa giải quyết đúng pain của họ.
                </span>
              </label>

              <label className="block">
                <span className="text-sm font-medium text-m2-on-surface">Outcomes (title|detail mỗi dòng)</span>
                <textarea
                  className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2 font-mono text-sm"
                  rows={4}
                  value={(landing.outcomes || []).map((o) => `${o.title}|${o.detail}`).join('\n')}
                  onChange={(e) =>
                    setLanding((p) => ({
                      ...p,
                      outcomes: e.target.value.split('\n').map((line) => {
                        const [title, ...rest] = line.split('|')
                        return { title: title || '', detail: rest.join('|') || '' }
                      }),
                    }))
                  }
                />
              </label>

              <label className="block">
                <span className="text-sm font-medium text-m2-on-surface">Before/After (task|before|after|saving)</span>
                <textarea
                  className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2 font-mono text-sm"
                  rows={3}
                  value={(landing.beforeAfter || [])
                    .map((r) => `${r.task}|${r.before}|${r.after}|${r.saving}`)
                    .join('\n')}
                  onChange={(e) =>
                    setLanding((p) => ({
                      ...p,
                      beforeAfter: e.target.value.split('\n').map((line) => {
                        const [task, before, after, saving] = line.split('|')
                        return {
                          task: task || '',
                          before: before || '',
                          after: after || '',
                          saving: saving || '',
                        }
                      }),
                    }))
                  }
                />
              </label>

              <label className="block">
                <span className="text-sm font-medium text-m2-on-surface">Demo URLs (mỗi dòng một URL)</span>
                <textarea
                  className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2 font-mono text-sm"
                  rows={2}
                  value={(landing.demoUrls || []).join('\n')}
                  onChange={(e) =>
                    setLanding((p) => ({
                      ...p,
                      demoUrls: e.target.value.split('\n'),
                    }))
                  }
                />
              </label>

              <label className="block">
                <span className="text-sm font-medium text-m2-on-surface">FAQ (câu hỏi|câu trả lời mỗi dòng)</span>
                <textarea
                  className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2 font-mono text-sm"
                  rows={4}
                  value={(landing.faqs || []).map((f) => `${f.q}|${f.a}`).join('\n')}
                  onChange={(e) =>
                    setLanding((p) => ({
                      ...p,
                      faqs: e.target.value.split('\n').map((line) => {
                        const [q, ...rest] = line.split('|')
                        return { q: q || '', a: rest.join('|') || '' }
                      }),
                    }))
                  }
                />
              </label>

              <div className="border-t border-m2-outline-variant pt-4 space-y-4">
                <h3 className="text-base font-semibold text-m2-on-surface">Giảng viên (block trang khóa)</h3>
                <p className="text-xs text-m2-on-surface-variant">
                  Ảnh paste/upload lên GCS. Hiển thị layout 2 cột trên trang chi tiết khóa (Mentor).
                </p>

                <div>
                  <span className="text-sm font-medium text-m2-on-surface">Ảnh giảng viên</span>
                  <div
                    tabIndex={0}
                    onPaste={handleInstructorPaste}
                    className="mt-1 border-2 border-dashed border-m2-outline rounded-lg p-4 min-h-[140px] focus:outline-none focus:ring-2 focus:ring-m2-primary bg-m2-surface-low"
                  >
                    {landing.instructor?.imageUrl ? (
                      <div className="relative inline-block max-w-full">
                        <img
                          src={landing.instructor.imageUrl}
                          alt="Giảng viên"
                          className="max-h-48 rounded-md object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => setInstructor({ imageUrl: '' })}
                          className="absolute top-1 right-1 bg-m2-surface/90 rounded-full p-1 shadow hover:bg-m2-surface-container"
                          title="Xóa ảnh"
                        >
                          <XMarkIcon className="h-4 w-4 text-m2-on-surface" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center text-m2-on-surface-variant py-6">
                        <PhotoIcon className="h-10 w-10 mb-2" />
                        <p className="text-sm">Copy-paste ảnh vào đây — lưu lên Google Cloud Storage.</p>
                      </div>
                    )}
                    <div className="mt-3 flex items-center gap-3">
                      <button
                        type="button"
                        disabled={uploadingInstructor}
                        onClick={() => instructorInputRef.current?.click()}
                        className="text-sm px-3 py-1.5 bg-m2-surface-container border border-m2-outline rounded-md hover:bg-m2-surface-high disabled:opacity-50"
                      >
                        {uploadingInstructor ? 'Đang tải...' : 'Chọn file ảnh'}
                      </button>
                      <input
                        ref={instructorInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) void handleInstructorFile(file)
                          e.target.value = ''
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-sm font-medium text-m2-on-surface">Chữ trên ảnh</span>
                    <input
                      type="text"
                      className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                      value={landing.instructor?.imageOverlay || ''}
                      onChange={(e) => setInstructor({ imageOverlay: e.target.value })}
                      placeholder="VD: BA thực chiến V2"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-m2-on-surface">Tiêu đề section</span>
                    <input
                      type="text"
                      className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                      value={landing.instructor?.sectionLabel || ''}
                      onChange={(e) => setInstructor({ sectionLabel: e.target.value })}
                      placeholder="GIẢNG VIÊN HƯỚNG DẪN"
                    />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="text-sm font-medium text-m2-on-surface">Tên giảng viên</span>
                    <input
                      type="text"
                      className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                      value={landing.instructor?.name || ''}
                      onChange={(e) => setInstructor({ name: e.target.value })}
                      placeholder="Lê Anh Tuấn"
                    />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="text-sm font-medium text-m2-on-surface">Bio</span>
                    <textarea
                      className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                      rows={4}
                      value={landing.instructor?.bio || ''}
                      onChange={(e) => setInstructor({ bio: e.target.value })}
                      placeholder="Mô tả ngắn về giảng viên..."
                    />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="text-sm font-medium text-m2-on-surface">
                      Stats (value|label mỗi dòng)
                    </span>
                    <textarea
                      className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2 font-mono text-sm"
                      rows={3}
                      value={(landing.instructor?.stats || [])
                        .map((s) => `${s.value}|${s.label}`)
                        .join('\n')}
                      onChange={(e) =>
                        setInstructor({
                          stats: e.target.value.split('\n').map((line) => {
                            const [value, ...rest] = line.split('|')
                            return { value: value || '', label: rest.join('|') || '' }
                          }),
                        })
                      }
                      placeholder={'5000+|HỌC VIÊN TỐT NGHIỆP\n50+|DỰ ÁN AI THỰC TẾ'}
                    />
                  </label>
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="space-y-6">
          <div className="flex items-center justify-between border-b pb-2">
            <h2 className="text-lg font-semibold text-m2-on-surface">Nội dung HTML</h2>
            {uploadingImage && <span className="text-xs text-m2-primary">Đang upload ảnh...</span>}
          </div>
          <p className="text-xs text-m2-on-surface-variant">
            Có thể copy-paste ảnh/bảng vào editor. Icon bảng trên toolbar để chèn mới; click vào bảng rồi dùng +C/−C/+H/−H; kéo mép ô để giãn cột/hàng.
          </p>
          {HTML_FIELDS.map(({ key, label }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-m2-on-surface mb-2">{label}</label>
              <div className="bg-m2-surface-container">
                <AdminRichTextEditor
                  value={form[key] as string}
                  onChange={(html) => handleHtmlChange(key, html)}
                  onUploadingChange={setUploadingImage}
                />
              </div>
            </div>
          ))}
        </section>

        {isEdit && (
          <section className="space-y-4 rounded-lg border border-amber-300/60 bg-amber-50/40 p-4">
            <div className="border-b border-amber-200 pb-2">
              <h2 className="text-lg font-semibold text-m2-on-surface">Khóa học tặng kèm</h2>
              <p className="text-xs text-m2-on-surface-variant mt-1">
                Khi thanh toán thành công khóa này, hệ thống tạo đăng ký khóa tặng (0đ). Học viên tự chọn lớp
                (quá khứ hoặc tương lai) trong Khóa học của tôi trong vòng 6 tháng.
              </p>
            </div>
            {giftRules.map((rule, idx) => (
              <div
                key={idx}
                className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end rounded-md border border-m2-outline-variant bg-m2-surface p-3"
              >
                <label className="block">
                  <span className="text-sm font-medium">Khóa được tặng (B)</span>
                  <select
                    className="mt-1 w-full border border-m2-outline rounded-md px-3 py-2"
                    value={rule.giftCourseId || ''}
                    onChange={(e) => {
                      const giftCourseId = Number(e.target.value) || 0
                      setGiftRules((prev) =>
                        prev.map((r, i) => (i === idx ? { ...r, giftCourseId } : r))
                      )
                    }}
                  >
                    <option value="">— Chọn khóa —</option>
                    {allCourses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.code} — {c.name}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={rule.isActive}
                      onChange={(e) =>
                        setGiftRules((prev) =>
                          prev.map((r, i) => (i === idx ? { ...r, isActive: e.target.checked } : r))
                        )
                      }
                    />
                    Đang bật
                  </label>
                  <button
                    type="button"
                    className="text-sm text-m2-error hover:underline"
                    onClick={() => setGiftRules((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    Xóa
                  </button>
                </div>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm border border-m2-outline rounded-md hover:bg-m2-surface-low"
                onClick={() => setGiftRules((prev) => [...prev, { giftCourseId: 0, isActive: true }])}
              >
                + Thêm khóa tặng
              </button>
              <button
                type="button"
                disabled={savingGifts}
                className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50"
                onClick={async () => {
                  const valid = giftRules.filter((r) => r.giftCourseId > 0)
                  if (giftRules.length > 0 && valid.length !== giftRules.length) {
                    toast.error('Chọn khóa được tặng cho mỗi dòng')
                    return
                  }
                  try {
                    setSavingGifts(true)
                    const res = await AdminApi.saveCourseGiftRules(
                      courseId,
                      valid.map((r) => ({ giftCourseId: r.giftCourseId, isActive: r.isActive }))
                    )
                    if (!res.success) {
                      toast.error(res.message || 'Lưu tặng kèm thất bại')
                      return
                    }
                    toast.success('Đã lưu khóa tặng kèm')
                  } catch (err) {
                    console.error(err)
                    toast.error('Không lưu được khóa tặng kèm (cần chạy migration DB)')
                  } finally {
                    setSavingGifts(false)
                  }
                }}
              >
                {savingGifts ? 'Đang lưu...' : 'Lưu tặng kèm'}
              </button>
            </div>
          </section>
        )}

        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <Link
            to={isEdit ? `/admin/courses/${courseId}` : '/admin/courses'}
            className="px-4 py-2 border border-m2-outline rounded-md text-m2-on-surface hover:bg-m2-surface-low"
          >
            Hủy
          </Link>
          <button
            type="submit"
            disabled={saving || uploadingCover || uploadingInstructor}
            className="px-4 py-2 bg-m2-primary text-m2-on-primary rounded-md hover:bg-eh-forest-shadow disabled:opacity-50"
          >
            {saving ? 'Đang lưu...' : isEdit ? 'Cập nhật' : 'Tạo khóa học'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default AdminCourseFormPage
